import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Clock } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function SchedulingManager({ agentId }) {
  const [schedule, setSchedule] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [showTimeOffForm, setShowTimeOffForm] = useState(false);
  const [timeOffData, setTimeOffData] = useState({ start_date: '', end_date: '', reason: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (agentId) {
      fetchSchedule();
      fetchTimeOff();
    }
  }, [agentId]);

  const fetchSchedule = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE}/dialer/agents/${agentId}/schedule`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSchedule(response.data);
    } catch (error) {
      console.error('Error fetching schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeOff = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE}/dialer/time-off?agent_id=${agentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTimeOffRequests(response.data);
    } catch (error) {
      console.error('Error fetching time off:', error);
    }
  };

  const handleRequestTimeOff = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE}/dialer/agents/${agentId}/time-off`,
        timeOffData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTimeOff();
      setShowTimeOffForm(false);
      setTimeOffData({ start_date: '', end_date: '', reason: '' });
    } catch (error) {
      console.error('Error requesting time off:', error);
      alert('Failed to request time off');
    }
  };

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-6">
      <h3 className="text-xl font-semibold flex items-center gap-2">
        <Calendar className="w-6 h-6" />
        Scheduling
      </h3>

      <div>
        <h4 className="font-semibold mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4" />
          Weekly Schedule
        </h4>
        {loading ? (
          <div className="text-center py-4">Loading schedule...</div>
        ) : schedule.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No schedule set</div>
        ) : (
          <div className="space-y-2">
            {schedule.map((entry) => (
              <div key={entry.id} className="border rounded p-3">
                <p className="font-medium">{daysOfWeek[entry.day_of_week]}</p>
                <p className="text-sm text-gray-600">
                  {entry.start_time} - {entry.end_time}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold">Time Off Requests</h4>
          <button
            onClick={() => setShowTimeOffForm(!showTimeOffForm)}
            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
          >
            Request Time Off
          </button>
        </div>

        {showTimeOffForm && (
          <form onSubmit={handleRequestTimeOff} className="border rounded p-4 space-y-3 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Start Date</label>
              <input
                type="date"
                value={timeOffData.start_date}
                onChange={(e) => setTimeOffData({ ...timeOffData, start_date: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">End Date</label>
              <input
                type="date"
                value={timeOffData.end_date}
                onChange={(e) => setTimeOffData({ ...timeOffData, end_date: e.target.value })}
                className="w-full border rounded px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Reason</label>
              <textarea
                value={timeOffData.reason}
                onChange={(e) => setTimeOffData({ ...timeOffData, reason: e.target.value })}
                className="w-full border rounded px-3 py-2"
                rows={2}
              />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                Submit
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowTimeOffForm(false);
                  setTimeOffData({ start_date: '', end_date: '', reason: '' });
                }}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {timeOffRequests.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No time off requests</div>
        ) : (
          <div className="space-y-2">
            {timeOffRequests.map((request) => (
              <div key={request.id} className="border rounded p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">
                      {new Date(request.start_date).toLocaleDateString()} - {new Date(request.end_date).toLocaleDateString()}
                    </p>
                    {request.reason && (
                      <p className="text-sm text-gray-600">{request.reason}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${
                    request.status === 'approved' ? 'bg-green-100 text-green-800' :
                    request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {request.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

